import React, { useRef, useEffect, useState, useCallback } from "react";
import "./RichEmailEditor.css";

// RichEmailEditor: a WYSIWYG drop-in replacement for the email textarea.
//
// CRITICAL CONTRACT (do not break): this is a pure editing surface. It takes
// the SAME plaintext value string the textarea used (plain text, newline line
// breaks, image placeholders and bold/italic markers) and emits the SAME
// string via onChange. Everything downstream (send, convertToHtml, footer, CID
// embedding, backend jobs, queued/scheduled mails) receives a character
// identical string and behaves exactly as before.
//
// Two visual affordances vs the textarea:
//   1. An image placeholder token renders as the real inline image (or a
//      loading/error chip) that the user can drag, regenerate or remove.
//   2. Bold/italic markdown markers (**…** / _…_) render as real bold/italic
//      in the editor — a true WYSIWYG. On the wire the value string still
//      carries the plaintext markers, because the send pipeline
//      (convertToHtml → applyInlineFormatting) converts them to <strong>/<em>
//      tags at send time, and prepared/queued/scheduled mails and previews
//      already assume markers, not tags. So we render markers → tags on DOM
//      hydration, and serialize tags → markers on the way out.
//
// Round-trip: value string -> stringToHtml -> contentEditable DOM, and DOM ->
// serialize -> value string (text nodes, br = newline, chip = token,
// <strong>/<b> = **…**, <em>/<i> = _…_). We only re-render the DOM from value
// when it changes for an EXTERNAL reason (generation, contact switch, remove).
// While the user types we never clobber the DOM, so the caret is preserved.

const IMAGE_TOKEN_RE = /\{\{\s*image:([a-zA-Z0-9_-]+)\s*\}\}/g;

const isBoldEl = (el) => {
  if (!el || el.nodeType !== 1) return false;
  const t = el.tagName;
  return t === "B" || t === "STRONG";
};
const isItalicEl = (el) => {
  if (!el || el.nodeType !== 1) return false;
  const t = el.tagName;
  return t === "I" || t === "EM";
};

// Mirror of applyInlineFormatting() in EmailFooterGenerator.js so the editor
// and the send pipeline agree on which marker runs form bold/italic.
// Kept intentionally conservative: markers must hug non-whitespace, and
// underscores need word-boundary context so snake_case / URLs are safe.
const applyMarkdownEmphasis = (escapedHtml) =>
  escapedHtml
    .replace(/\*\*(?=\S)([\s\S]*?\S)\*\*/g, "<strong>$1</strong>")
    .replace(
      /(^|[\s.,;:!?()"'>*])_(?=\S)([\s\S]*?\S)_(?=$|[\s.,;:!?()"'<*])/g,
      "$1<em>$2</em>"
    );

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeAttr = (s) => escapeHtml(s).replace(/"/g, "&quot;");

// ── Serialize the editable DOM back into the plaintext body string ──────────
// Order-preserving walk. Text -> text, <br> -> "\n", block (<div>/<p>) -> a
// leading "\n" before its content, image chip -> {{image:token}}, bold
// wrapper (<b>/<strong>) -> **…**, italic wrapper (<i>/<em>) -> _…_.
const serialize = (root) => {
  let out = "";
  const walk = (node, ctx) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.nodeValue;
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;
      const el = child;
      const token = el.getAttribute && el.getAttribute("data-token");
      if (token) {
        out += `{{image:${token}}}`;
        return;
      }
      const tag = el.tagName;
      if (tag === "BR") {
        out += "\n";
        return;
      }
      if (tag === "DIV" || tag === "P") {
        // A block element implies a line break before its content (except when
        // it is the very first thing or we already ended with a newline).
        if (out.length && !out.endsWith("\n")) out += "\n";
        walk(el, ctx);
        return;
      }
      // Bold wrapper -> wrap inner content with **…**. Skip when we're already
      // inside a bold context (avoids "****foo****" for nested wrappers).
      if (isBoldEl(el)) {
        if (ctx.bold) {
          walk(el, ctx);
          return;
        }
        const before = out.length;
        walk(el, { ...ctx, bold: true });
        const inner = out.slice(before);
        // Only wrap if there's actual non-whitespace content; empty bold
        // wrappers (e.g. a caret-parked <b> right after a Cmd+B) would emit a
        // stray "****" otherwise.
        if (/\S/.test(inner)) {
          out = out.slice(0, before) + "**" + inner + "**";
        }
        return;
      }
      if (isItalicEl(el)) {
        if (ctx.italic) {
          walk(el, ctx);
          return;
        }
        const before = out.length;
        walk(el, { ...ctx, italic: true });
        const inner = out.slice(before);
        if (/\S/.test(inner)) {
          out = out.slice(0, before) + "_" + inner + "_";
        }
        return;
      }
      // Any other inline wrapper (span, font, …): recurse transparently.
      walk(el, ctx);
    });
  };
  walk(root, { bold: false, italic: false });
  // Normalize artefacts that never exist in the textarea model.
  out = out.replace(/\u00a0/g, " ").replace(/\u200b/g, "");

  // Fully-empty editor (only a browser filler <br> / whitespace) => empty body.
  const hasReal = root.textContent.replace(/\u200b/g, "").trim().length > 0 || !!root.querySelector("[data-token]");
  if (!hasReal && !/[^\n]/.test(out)) return "";
  return out;
};

// Resolve a token to a render state, merging per-recipient AI images (images)
// over the ImageLab asset catalog (assets). Returns null for tokens that match
// no known asset — those are left as literal {{image:token}} text so nothing
// looks broken and the string round-trips untouched.
const resolveState = (token, images, assets) => {
  const ai = images && images[token];
  if (ai) return { ...ai, isAi: true };
  const asset = assets && assets[token];
  if (!asset) return null;
  if (asset.type === "ai" || asset.type === "miniApp") {
    // Dynamic image not generated yet for this recipient.
    return { status: "loading", name: asset.name, isAi: true };
  }
  if (asset.imageUrl) {
    return {
      status: "ready",
      imageUrl: asset.imageUrl,
      name: asset.name,
      maxWidth: asset.maxWidth,
      align: asset.align,
      isAi: false,
    };
  }
  return null;
};

// ── Chip (inline image) markup ──────────────────────────────────────────────
const chipInnerHtml = (token, state) => {
  const status = (state && state.status) || "loading";
  const isAi = !!(state && state.isAi);
  const name = escapeAttr((state && state.name) || token);
  let media;
  if (status === "ready" && state && state.imageUrl) {
    media = `<img class="rte-img-el" src="${escapeAttr(state.imageUrl)}" alt="${name}" draggable="false" />`;
  } else if (status === "error") {
    media = `<span class="rte-img-state rte-img-state--error"><i class="fa-solid fa-triangle-exclamation"></i><span>${escapeHtml(
      (state && state.error) || "Generation failed"
    )}</span></span>`;
  } else {
    // Apple-style, CSS-only loading experience: pulsing orb, cycling status
    // copy and an eased progress bar that slows near the end — so the wait
    // feels alive without any JS timers fighting the contentEditable DOM.
    media =
      `<span class="rte-img-loading" aria-live="polite" aria-label="Generating image">` +
      `<span class="rte-img-loading-head">` +
      `<span class="rte-img-loading-orb"><i class="fa-solid fa-wand-magic-sparkles"></i></span>` +
      `</span>` +
      `<span class="rte-img-loading-bar"><span class="rte-img-loading-fill"></span></span>` +
      `</span>`;
  }

  const enlargeBtn =
    status === "ready" && state && state.imageUrl
      ? `<button type="button" class="rte-img-btn" data-action="enlarge" data-token="${escapeAttr(
          token
        )}" title="Enlarge"><i class="fa-solid fa-up-right-and-down-left-from-center"></i></button>`
      : "";

  // Refine (AI edit): only for AI images that already have a rendered result —
  // the user describes a change and we edit THIS image, ChatGPT-style.
  const refineBtn =
    isAi && status === "ready" && state && state.imageUrl
      ? `<button type="button" class="rte-img-btn" data-action="refine" data-token="${escapeAttr(
          token
        )}" title="Edit with AI"><i class="fa-solid fa-wand-magic-sparkles"></i></button>`
      : "";

  // Regenerate only makes sense for AI images; static ImageLab assets omit it.
  const regenBtn = isAi
    ? `<button type="button" class="rte-img-btn" data-action="regenerate" data-token="${escapeAttr(
        token
      )}" title="Regenerate"${status === "loading" ? " disabled" : ""}><i class="fa-solid fa-rotate"></i></button>`
    : "";

  return `${media}<span class="rte-img-tools" contenteditable="false">${enlargeBtn}${refineBtn}${regenBtn}<button type="button" class="rte-img-btn rte-img-btn--danger" data-action="remove" data-token="${escapeAttr(
    token
  )}" title="Remove image"><i class="fa-solid fa-xmark"></i></button></span>`;
};

const chipHtml = (token, state) => {
  const status = (state && state.status) || "loading";
  return `<span class="rte-img" data-token="${escapeAttr(
    token
  )}" data-status="${status}" data-status-url="${escapeAttr(String(state && state.imageUrl))}" contenteditable="false" draggable="true">${chipInnerHtml(
    token,
    state
  )}</span>`;
};

// ── value string -> HTML for the contentEditable ───────────────────────────
const stringToHtml = (value, images, assets) => {
  const src = typeof value === "string" ? value : "";
  let html = "";
  let last = 0;
  let m;
  IMAGE_TOKEN_RE.lastIndex = 0;
  // Order matters: escape HTML first so nothing in the user text is a real
  // tag, then apply the markdown emphasis pass (introduces <strong>/<em>),
  // then convert newlines to <br>. This mirrors applyInlineFormatting() in
  // EmailFooterGenerator.js so the on-screen rendering matches what the
  // recipient will see.
  const textToHtml = (t) => applyMarkdownEmphasis(escapeHtml(t)).replace(/\n/g, "<br>");
  while ((m = IMAGE_TOKEN_RE.exec(src)) !== null) {
    if (m.index > last) html += textToHtml(src.slice(last, m.index));
    const token = m[1];
    const st = resolveState(token, images, assets);
    // Unknown token -> keep the literal placeholder text (escaped).
    html += st ? chipHtml(token, st) : textToHtml(m[0]);
    last = m.index + m[0].length;
  }
  html += textToHtml(src.slice(last));
  return html;
};

const placeCaretAtEnd = (el) => {
  try {
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  } catch (_) {
    /* noop */
  }
};

const RichEmailEditor = ({
  value = "",
  onChange,
  images = {},
  assets = {},
  placeholder = "",
  className = "",
  disabled = false,
  onRegenerate,
  onRemove,
  onRefine,
}) => {
  const rootRef = useRef(null);
  const lastEmittedRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);
  // Refine panel: { token, imageUrl, name } while the user describes an edit.
  const [refinePanel, setRefinePanel] = useState(null);
  const [refineText, setRefineText] = useState("");

  // Keep the latest images/assets accessible to effects/handlers w/o re-binding.
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const assetsRef = useRef(assets);
  assetsRef.current = assets;

  const emitChange = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const str = serialize(root);
    lastEmittedRef.current = str;
    if (onChange) onChange(str);
  }, [onChange]);

  // Render DOM from value ONLY when value changed for an external reason
  // (i.e. it differs from what we last emitted). Never clobber while typing.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (value === lastEmittedRef.current) return;
    const wasFocused = document.activeElement === root;
    root.innerHTML = stringToHtml(value, imagesRef.current, assetsRef.current);
    lastEmittedRef.current = value;
    if (wasFocused) placeCaretAtEnd(root);
  }, [value]);

  // When image states change (loading -> ready/error), update the existing
  // chips IN PLACE so the caret / typing are never disturbed. No-op when the
  // resolved state is unchanged, so this is safe to run on every render.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.querySelectorAll("[data-token]").forEach((el) => {
      const token = el.getAttribute("data-token");
      const state = resolveState(token, images, assets);
      const status = (state && state.status) || "loading";
      const url = String(state && state.imageUrl);
      if (el.getAttribute("data-status") === status && el.getAttribute("data-status-url") === url) {
        return; // unchanged
      }
      el.setAttribute("data-status", status);
      el.setAttribute("data-status-url", url);
      el.innerHTML = chipInnerHtml(token, state);
    });
  }, [images, assets]);

  const handleKeyDown = (e) => {
    // Bold / italic: apply real formatting inline so the user sees bold/italic
    // immediately. serialize() converts the resulting <b>/<i> wrappers back
    // into **…** / _…_ markers on the way out, so the value string the send
    // pipeline receives is unchanged from the old textarea behaviour.
    if ((e.metaKey || e.ctrlKey) && !e.altKey) {
      const k = e.key.toLowerCase();
      if (k === "b" || k === "i") {
        e.preventDefault();
        // Force <b>/<i> tags (vs styled spans) so serialize() has a stable
        // signal to detect. Safe to call repeatedly; browsers cache it.
        try {
          document.execCommand("styleWithCSS", false, false);
        } catch (_) {
          /* legacy browsers */
        }
        document.execCommand(k === "b" ? "bold" : "italic");
        // execCommand doesn't always fire an input event with no selection
        // (it just toggles state for the next keystroke). Emit anyway so the
        // parent value stays in sync when text was actually wrapped.
        emitChange();
        return;
      }
    }
    // Enter -> a single line break (<br>), never a block <div>/<p>.
    if (e.key === "Enter") {
      e.preventDefault();
      document.execCommand("insertLineBreak");
    }
  };

  // Normalize on blur: manually-typed **foo** / _foo_ markers become real
  // bold/italic. We reuse stringToHtml on the just-serialized value so the
  // DOM matches exactly what the send pipeline will produce.
  const handleBlur = () => {
    const root = rootRef.current;
    if (!root) return;
    const str = serialize(root);
    // Only rewrite the DOM if the value contains markers that would render
    // differently than what's currently on screen — avoids caret churn and
    // unnecessary reflows when the user just tabbed away from a plain
    // paragraph.
    if (!/\*\*|_/.test(str)) return;
    const nextHtml = stringToHtml(str, imagesRef.current, assetsRef.current);
    if (root.innerHTML !== nextHtml) {
      root.innerHTML = nextHtml;
      lastEmittedRef.current = str;
    }
  };

  // Force plain-text paste so no foreign HTML enters the model.
  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  // Block dropping external files/images (they'd have no token and be lost);
  // internal chip drag-to-move stays allowed.
  const handleDrop = (e) => {
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      e.preventDefault();
    }
  };

  const handleMouseDown = (e) => {
    // Prevent caret loss / drag start when clicking a chip control button.
    if (e.target.closest && e.target.closest("[data-action]")) e.preventDefault();
  };

  const handleClick = (e) => {
    const btn = e.target.closest && e.target.closest("[data-action]");
    if (!btn) return;
    e.preventDefault();
    const token = btn.getAttribute("data-token");
    const action = btn.getAttribute("data-action");
    if (action === "remove") {
      if (onRemove) onRemove(token);
    } else if (action === "regenerate") {
      if (onRegenerate) onRegenerate(token);
    } else if (action === "enlarge") {
      const st = imagesRef.current && imagesRef.current[token];
      if (st && st.imageUrl) setLightbox(st);
    } else if (action === "refine") {
      const st = resolveState(token, imagesRef.current, assetsRef.current);
      if (st && st.imageUrl) {
        setRefineText("");
        setRefinePanel({ token, imageUrl: st.imageUrl, name: st.name || token });
      }
    }
  };

  const closeRefine = () => {
    setRefinePanel(null);
    setRefineText("");
  };

  const submitRefine = () => {
    const text = refineText.trim();
    if (!text || !refinePanel) return;
    const token = refinePanel.token;
    closeRefine();
    if (onRefine) onRefine(token, text);
  };

  const isEmpty = !value || value.length === 0;

  return (
    <>
      <div
        ref={rootRef}
        className={`rte-editor ${className}`}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-empty={isEmpty ? "true" : "false"}
        data-placeholder={placeholder}
        spellCheck={true}
        onInput={emitChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onBlur={handleBlur}
      />

      {lightbox && lightbox.imageUrl && (
        <div className="rte-lightbox" onClick={() => setLightbox(null)}>
          <button className="rte-lightbox-close" onClick={() => setLightbox(null)} title="Close">
            <i className="fa-solid fa-xmark" />
          </button>
          <img src={lightbox.imageUrl} alt={lightbox.name || ""} onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {refinePanel && refinePanel.imageUrl && (
        <div className="rte-refine" onClick={closeRefine}>
          <div className="rte-refine-card" onClick={(e) => e.stopPropagation()}>
            <button className="rte-refine-close" onClick={closeRefine} title="Close">
              <i className="fa-solid fa-xmark" />
            </button>
            <div className="rte-refine-preview">
              <img src={refinePanel.imageUrl} alt={refinePanel.name || ""} />
            </div>
            <div className="rte-refine-body">
              <div className="rte-refine-head">
                <span className="rte-refine-eyebrow">
                  <i className="fa-solid fa-wand-magic-sparkles" /> Edit with AI
                </span>
                <h4 className="rte-refine-title">Refine this image</h4>
                <p className="rte-refine-sub">
                  Describe what to change — a new version is generated from the current image, keeping the rest intact.
                </p>
              </div>
              <textarea
                className="rte-refine-input"
                rows={4}
                value={refineText}
                autoFocus
                onChange={(e) => setRefineText(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                    e.preventDefault();
                    submitRefine();
                  }
                }}
                placeholder="e.g. Make the background brighter, add our logo top-left, and use warmer colors"
              />
              <div className="rte-refine-actions">
                <button type="button" className="rte-refine-cancel" onClick={closeRefine}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="rte-refine-submit"
                  onClick={submitRefine}
                  disabled={!refineText.trim()}
                >
                  <i className="fa-solid fa-wand-magic-sparkles" /> Generate new version
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RichEmailEditor;
